/**
 * plume_material.ts — the SHARED thruster plume (fire realism R9, 2026-09-25).
 * The hovercraft (lib/vehicle_fx.ts) wears it today; the SPACE agent imports
 * it for ship plumes — the factory signature below is the contract.
 *
 * A flat translucent cone is what every plume was: no structure, no shock
 * cells, the same colour end to end. This is a TSL plume on the SAME unit cone
 * (mouth at z = 0, tail at z = 1 — `plumeGeometry`):
 *
 *   · AXIAL position s = positionGeometry.z (measured in geometry space — the
 *     positionGeometry law — so any scale or instancing leaves it intact);
 *   · RADIAL falloff = |N·V|^1.5 — the chord a view ray cuts through a round
 *     jet is ∝ |N·V| at the surface point, so the plume is dense on its axis
 *     and thins to nothing at its silhouette (DoubleSide: front and back add);
 *   · a SCROLLING NOISE TONGUE from the fire system's baked 3D noise (one
 *     fetch), which breaks the cone's edges into a licking exhaust;
 *   · MACH DIAMONDS — periodic axial brightness cells ≈ 1.35 nozzle radii
 *     apart (lib/vfx/fire_rules.ts `machDiamonds`, the CPU mirror the harness
 *     pins), sharp knots decaying along the axis, strongest on the axis, and
 *     only at high throttle (∝ throttle²);
 *   · a palette: 'tint' (the model's afterburner colour with a white-hot core
 *     near the mouth) or 'blackbody' (the axial temperature 1,600 → 900 K
 *     through the same Planck LUT the fires use).
 * Output is PREMULTIPLIED with α = 0 — pure emission under CustomBlending
 * (One, OneMinusSrcAlpha) — linear HDR (the bloom picks the diamonds up).
 * `markMrtOverlay` (the exhaust is drawn over the hull and the ground).
 *
 * FACTORY (the contract):
 *   const plume = createPlumeMaterial({ color, nozzleRadius, palette?, coreColor?, intensity?, diamondSpacing?, noiseSpeed? });
 *   mesh = new THREE.Mesh(plumeGeometry(), plume.material);   // scale.z = the plume's length in metres
 *   plume.set({ throttle, length, boost, color? });            // per frame (uniform writes only)
 * One material per nozzle SET (all nozzles of a ride share throttle/length); materials are cheap uniform holders,
 * but each is its own pipeline — share one per ship class where the throttles agree.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, texture, texture3D, positionGeometry, positionWorld, cameraPosition, normalWorld,
  abs, dot, normalize, pow, exp, cos, smoothstep, mix, time, max,
} from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { fireTextures } from './fire_volume.js';
import { MACH_SPACING_R, MACH_SHARPNESS, MACH_DECAY_CELLS } from './fire_rules.js';

/* eslint-disable @typescript-eslint/no-explicit-any */
type N = any;

export interface PlumeOptions {
  /** The exhaust tint (the 'tint' palette's afterburner colour). */
  color: THREE.ColorRepresentation;
  /** Nozzle radius (m): the Mach diamond spacing is `diamondSpacing` × this. */
  nozzleRadius: number;
  /** 'tint' (default) or 'blackbody' (axial temperature through the Planck LUT; `color` then only tints the diamonds). */
  palette?: 'tint' | 'blackbody';
  /** The white-hot core near the mouth (default near-white). */
  coreColor?: THREE.ColorRepresentation;
  /** Linear-HDR scale (default 5). */
  intensity?: number;
  /**
   * §4.3 (surfaces, 2026-09-27): ON THE PHYSICAL CURVE — the glow × `uPlumeEmission` (the fire system writes
   * emitterScene(600 cd/m², held at display 1.3) / PLUME_REF each frame): a jet's glow is faint by day and held with the
   * flame at night. Opt-in: the hovercraft passes true; ship plumes (the space agent's own eye) keep their units.
   */
  physical?: boolean;
  /** Diamond spacing in nozzle radii (default MACH_SPACING_R = 1.35). */
  diamondSpacing?: number;
  /** How fast the noise tongue scrolls down the plume (default 2.2 plume-lengths/s). */
  noiseSpeed?: number;
}

export interface PlumeHandle {
  material: THREE.NodeMaterial;
  uniforms: { throttle: { value: number }; length: { value: number }; boost: { value: number }; intensity: { value: number }; color: { value: THREE.Color }; core: { value: THREE.Color } };
  /** Per-frame state: throttle 0..1, the plume's length (m), boost (afterburner) on/off, an optional tint change. */
  set(s: { throttle: number; length: number; boost?: boolean; color?: THREE.ColorRepresentation }): void;
}

/**
 * The unit plume cone: mouth radius 1 at z = 0 tapering to 0.12 at z = 1,
 * open-ended, 16 × 6 segments (the lengthwise rows let the diamonds shade
 * smoothly). Scale x/y by the nozzle radius, z by the plume length.
 */
export function plumeGeometry(): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(0.12, 1, 1, 16, 6, true);
  g.translate(0, 0.5, 0);
  g.rotateX(Math.PI / 2); // +Y → +Z: the mouth (radius 1) at z = 0, the tail at z = 1
  g.deleteAttribute('uv');
  return g;
}

/** §4.3 (surfaces, 2026-09-27): the physical plumes' shared scale (1 = the pre-curve units). */
const uPlumeEmission = uniform(1);
/** The 'tint' plume's mouth-axis luminance at throttle 1, intensity 5, a white core — its own units' reference. */
export const PLUME_REF = 5;
export function setPlumeEmission(k: number): void { (uPlumeEmission as unknown as { value: number }).value = Number.isFinite(k) ? Math.max(0, k) : 1; }
export function plumeEmission(): number { return (uPlumeEmission as unknown as { value: number }).value; }

export function createPlumeMaterial(opts: PlumeOptions): PlumeHandle {
  const uThrottle = uniform(0);
  const uLength = uniform(1);
  const uBoost = uniform(0);
  const uIntensity = uniform(opts.intensity ?? 5);
  const uColor = uniform(new THREE.Color(opts.color));
  const uCore = uniform(new THREE.Color(opts.coreColor ?? 0xfff4e6));
  const nozzleR = Math.max(0.02, opts.nozzleRadius);
  const spacing = (opts.diamondSpacing ?? MACH_SPACING_R) * nozzleR;
  const speed = opts.noiseSpeed ?? 2.2;
  const blackbody = opts.palette === 'blackbody';
  const m = new THREE.NodeMaterial();
  m.name = blackbody ? 'plume-blackbody' : 'plume';
  m.side = THREE.DoubleSide;
  m.transparent = true;
  m.depthWrite = false;
  m.fog = false;
  m.blending = THREE.CustomBlending;
  m.blendSrc = THREE.OneFactor;
  m.blendDst = THREE.OneMinusSrcAlphaFactor;
  m.blendSrcAlpha = THREE.OneFactor;
  m.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
  m.colorNode = Fn(() => {
    const s = positionGeometry.z.clamp(0, 1);
    const V = normalize(cameraPosition.sub(positionWorld));
    const facing = abs(dot(normalWorld, V));
    const radial = pow(facing, float(1.5));
    // The jet thins and cools along its length; the tail fades out.
    const axial = exp(s.mul(-2.1)).mul(float(1).sub(smoothstep(0.72, 1.0, s)));
    // The tongue: the fire system's noise, scrolled down the plume.
    const n = texture3D(fireTextures().noise, vec3(positionGeometry.x.mul(0.35), positionGeometry.y.mul(0.35), s.mul(1.4).sub((time as N).mul(speed)))).w;
    const tongue = mix(float(0.45), float(1.35), n).mul(mix(float(1), n.mul(1.6), s));
    // Mach diamonds: raised-cosine knots every `spacing` metres, decaying, on the axis, only when the engine is pushed.
    const phase = s.mul(uLength).div(spacing);
    const knots = pow(float(0.5).add(cos(phase.mul(Math.PI * 2)).mul(0.5)), float(MACH_SHARPNESS));
    const diamonds = knots.mul(exp(phase.negate().div(MACH_DECAY_CELLS))).mul(uThrottle.mul(uThrottle)).mul(pow(facing, float(3)));
    let body: N;
    if (blackbody) {
      const bb = texture(fireTextures().lut, vec2(float(1).sub(s.mul(0.9)), 0.5));
      body = bb.xyz.mul(bb.w);
    } else {
      body = mix(uCore, uColor, smoothstep(0.0, 0.35, s));
    }
    const glow = body.mul(axial.mul(radial).mul(tongue)).add(uCore.mul(diamonds).mul(float(1.6).add(uBoost)));
    const k0 = uIntensity.mul(float(0.2).add(uThrottle.mul(0.8))).mul(float(1).add(uBoost.mul(0.6)));
    const k = opts.physical ? k0.mul(uPlumeEmission) : k0; // §4.3 (surfaces, 2026-09-27)
    return vec4(max(glow.mul(k), vec3(0)), 0);
  })();
  markMrtOverlay(m);
  return {
    material: m,
    uniforms: { throttle: uThrottle as never, length: uLength as never, boost: uBoost as never, intensity: uIntensity as never, color: uColor as never, core: uCore as never },
    set(st) {
      (uThrottle as unknown as { value: number }).value = Math.min(1, Math.max(0, st.throttle));
      (uLength as unknown as { value: number }).value = Math.max(0.01, st.length);
      (uBoost as unknown as { value: number }).value = st.boost ? 1 : 0;
      if (st.color !== undefined) (uColor as unknown as { value: THREE.Color }).value.set(st.color);
    },
  };
}
